import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Guide } from 'types/guide';
import { api } from 'services/api';
import { Paginated } from 'types/paginated';
import { moneyFormat } from 'helpers/moneyFormat';
import { getGuideStatusLabel } from 'helpers/guideStatus';
import { GuideOrder, guideOrderParams, nextGuideOrder } from 'helpers/guideSort';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useApp } from 'hooks/app';
import { toast } from 'react-toastify';
import { checkDateRange } from 'helpers/dateRange';

export type GuideQueryParams = {
  patient_name: string;
  doctor_name: string;
  marker_id: number | null;
  final_date: Date | null;
  initial_date: Date | null;
  status: string;
};

export function useFetchGuides() {
  const { isOpenedMenu, handleOpenMenu } = useApp();
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [rows, setRows] = useState<number>(96);
  const [total, setTotal] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedGuide, setSelectedGuide] = useState<Guide | null>(null);
  const [viewGuide, setViewGuide] = useState<boolean>(false);
  const [canceling, setCanceling] = useState<boolean>(false);
  const [finishGuide, setFinishGuide] = useState<boolean>(false);
  const [noShowGuide, setNoShowGuide] = useState<boolean>(false);
  const [order, setOrder] = useState<GuideOrder | null>(null);
  const [cancelGuide, setCancelGuide] = useState<boolean>(false);
  const [filter, setFilter] = useState<GuideQueryParams>({
    doctor_name: '',
    patient_name: '',
    marker_id: null,
    initial_date: startOfMonth(new Date()),
    final_date: endOfMonth(new Date()),
    status: 'all',
  });

  function onExited() {
    setSelectedGuide(null);
    setFinishGuide(false);
    setNoShowGuide(false);
    setViewGuide(false);
    setCancelGuide(false);
    setAnchorEl(null);
  }

  function fetchData(nextPage = page, nextRows = rows, nextFilter = filter, nextOrder = order) {
    if (!checkDateRange(nextFilter)) return;

    let retrying = false;
    setLoading(true);

    if (isOpenedMenu) handleOpenMenu();

    api
      .get<Paginated<Guide[]>>('/guides', {
        params: {
          ...nextFilter,
          page: nextPage + 1,
          rows: nextRows,
          ...guideOrderParams(nextOrder),
          status: nextFilter.status === 'all' ? undefined : nextFilter.status,
        },
      })
      .then(response => {
        if (response.data.items.length === 0 && nextPage > 0) {
          // a página ficou vazia (ex.: última guia da última página mudou de status)
          retrying = true;
          setPage(nextPage - 1);
          fetchData(nextPage - 1, nextRows, nextFilter, nextOrder);
          return;
        }

        const data = formatGuideData(response.data.items);
        setTotal(response.data.total);

        setGuides(data);
        onExited();
      })
      .catch(() => toast.error('Não foi possível carregar as guias'))
      .finally(() => {
        if (!retrying) setLoading(false);
      });
  }

  function refresh() {
    fetchData(page, rows, filter);
  }

  function handleSearch(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    setPage(0);
    fetchData(0, rows, filter);
  }

  function handleChangePage(nextPage: number) {
    setPage(nextPage);
    fetchData(nextPage, rows, filter);
  }

  function handleChangeRows(nextRows: number) {
    setRows(nextRows);
    setPage(0);
    fetchData(0, nextRows, filter);
  }

  function handleSort(index: string) {
    const nextOrder = nextGuideOrder(index, order);
    if (!nextOrder) return;

    setOrder(nextOrder);
    setPage(0);
    fetchData(0, rows, filter, nextOrder);
  }

  function handleChange<K extends keyof GuideQueryParams>(index: K, value: GuideQueryParams[K]) {
    setFilter(state => ({
      ...state,
      [index]: value,
    }));
  }

  function handleFinishGuide() {
    if (!selectedGuide) return;

    api
      .put(`/guides/${selectedGuide.id}`, { status: 'completed' })
      .then(() => {
        toast.success('Guia concluída com sucesso!');
        refresh();
        onExited();
      })
      .catch(() => toast.error('Não foi possível concluir a guia'))
      .finally(() => setFinishGuide(false));
  }

  function handleNoShowGuide() {
    if (!selectedGuide) return;

    api
      .put(`/guides/${selectedGuide.id}`, { status: 'no_show' })
      .then(() => {
        toast.success('Falta registrada com sucesso!');
        refresh();
        onExited();
      })
      .catch(() => toast.error('Não foi possível registrar a falta'))
      .finally(() => setNoShowGuide(false));
  }

  function handleCancelGuide() {
    if (!selectedGuide) return;
    setCanceling(true);

    api
      .delete(`/guides/${selectedGuide.id}`)
      .then(() => {
        toast.success('Guia cancelada com sucesso!');
        refresh();
        onExited();
      })
      .catch(() => toast.error('Não foi possível cancelar a guia'))
      .finally(() => setCanceling(false));
  }

  const formatGuideData = useMemo(
    () =>
      (guides: Guide[]): Guide[] => {
        return guides.map(guide => ({
          ...guide,

          formattedMarkerName: guide.marker.name,
          formattedDoctorName: guide.doctor.name,
          formattedPatientName: guide.patient.name,
          formattedProcedureName: guide.procedure.name,
          formattedValueMarker: moneyFormat(guide.value_marker),
          formattedValueProcedure: moneyFormat(guide.value_procedure),
          formattedValueDiscount: moneyFormat(guide.value_discount),
          formattedValueTotal: moneyFormat(guide.value_total),
          formattedAppointmentDate: format(guide.appointment_date, 'Pp', { locale: ptBR }),
          formattedCreatedAt: format(guide.created_at, 'Pp', { locale: ptBR }),
          formattedStatus: getGuideStatusLabel(guide.status),
        }));
      },
    [ptBR],
  );

  useEffect(() => {
    fetchData(0, rows, filter);
  }, []);

  return {
    guides,
    loading,
    canceling,
    handleSort,
    order,
    filter,
    handleChange,
    setPage: handleChangePage,
    setRows: handleChangeRows,
    handleSearch,
    page,
    anchorEl,
    setAnchorEl,
    setSelectedGuide,
    setViewGuide,
    viewGuide,
    selectedGuide,
    cancelGuide,
    setCancelGuide,
    handleCancelGuide,
    onExited,
    finishGuide,
    setFinishGuide,
    handleFinishGuide,
    noShowGuide,
    setNoShowGuide,
    handleNoShowGuide,
    total,
    rows,
  };
}
